import { useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchOrder, fetchProductBySlug, fetchProducts, fetchSettings } from '@/api/endpoints';
import type { Product } from '@/api/models';

export const qk = {
  products: ['products'] as const,
  product: (slug: string) => ['product', slug] as const,
  settings: ['settings'] as const,
  cart: ['cart'] as const,
  order: (id: number, key: string) => ['order', id, key] as const,
};

export function useProducts() {
  const qc = useQueryClient();
  return useQuery({
    queryKey: qk.products,
    queryFn: async ({ signal }) => {
      const list = await fetchProducts(signal);
      // Seed each product page so opening one from the rail is instant.
      list.forEach((p) => qc.setQueryData(qk.product(p.slug), p));
      return list;
    },
    staleTime: 60_000,
  });
}

export function useProduct(slug: string) {
  const qc = useQueryClient();
  return useQuery<Product | null>({
    queryKey: qk.product(slug),
    queryFn: ({ signal }) => fetchProductBySlug(slug, signal),
    initialData: () => qc.getQueryData<Product[]>(qk.products)?.find((p) => p.slug === slug),
    staleTime: 60_000,
  });
}

export function useSettings() {
  return useQuery({ queryKey: qk.settings, queryFn: ({ signal }) => fetchSettings(signal), staleTime: 10 * 60_000 });
}

export function useOrder(id: number, key: string) {
  return useQuery({
    queryKey: qk.order(id, key),
    queryFn: ({ signal }) => fetchOrder(id, key, signal),
    enabled: !!id && !!key,
    retry: 1,
  });
}
